import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  ExportOutlined,
  FacebookOutlined,
  FileOutlined,
  FileTextOutlined,
  HomeOutlined,
  LinkedinOutlined,
  LoginOutlined,
  PrinterOutlined,
  RedditOutlined,
  ShareAltOutlined,
  TwitterOutlined,
  UserAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { BlobProvider, PDFDownloadLink, pdf } from "@react-pdf/renderer";
import {
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Input,
  Layout,
  Modal,
  Result,
  Row,
  Segmented,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { Whatsapp } from "@/Assets/Index";
import {
  ClassicTemplate as ClassicTemplateImage,
  CreativeTemplate as CreativeTemplateImage,
  ModernTemplate as ModernTemplateImage,
  PhotoTemplate as PhotoTemplateImage,
  SimpleBlushTemplate as SimpleBlushTemplateImage,
  ModernSlateTemplate as ModernSlateTemplateImage,
  ProfessionalBlueTemplate as ProfessionalBlueTemplateImage,
  ObsidianEdgeTemplate as ObsidianEdgeTemplateImage,
  CleanProfessionalTemplate as CleanProfessionalTemplateImage,
  MinimalistProTemplate as MinimalistProTemplateImage,
  PhotoClassicTemplate as PhotoClassicTemplateImage,
  ExecutiveSidebarTemplate as ExecutiveSidebarTemplateImage,
  TraditionalTemplateImage,
  ATSPrimeTemplateImage,
  SimpleATSTemplateImage,
} from "@/Assets/Index.js";
import AppLayout from "@/Components/Common/AppLayout";
import CustomizableResumePreview from "@/Components/Common/CustomizableResumePreview";
import ProfilePhotoUpload from "@/Components/Common/ProfilePhotoUpload";
import {
  AppleMusicDesktopCard,
  CareerStatsCard,
  DiscordDesktopCard,
  DuolingoCard,
  GitHubProfileCard,
  GitHubStyleCard,
  IMDBDesktopCard,
  InstagramStoryCard,
  NetflixStyleCard,
  SpotifyDesktopCard,
  SpotifyPodcastsDesktopCard,
  SpotifyWrappedCard,
  SpotifyWrappedMobileSummaryCard,
  TwitterMobileCard,
  YouTubeStudioCard,
  downloadCardAsImage,
} from "@/Components/Common/ShareableResumeCards";
import { useAuth } from "@/Context/AuthContext";
import { logger } from "@/Services/Logger/Index";
import { sortResumeForDisplay } from "@/Utils/DateSortingUtils";
import { formatDateToMMYYYY } from "@/Utils/Resume/DateFormattingUtils";
import { prepareResumeDataForPDF } from "@/Utils/Resume/ResumeDataManager";
import { loadCustomSectionOrder } from "@/Utils/SectionOrderingUtils";
import ClassicATSTemplate from "../../Templates/ClassicATS";
import ClassicCoverLetter from "../../Templates/CoverLetter/ClassicCoverLetter";
import CreativeCoverLetter from "../../Templates/CoverLetter/CreativeCoverLetter";
import ModernCoverLetter from "../../Templates/CoverLetter/ModernCoverLetter";
import PhotoCoverLetter from "../../Templates/CoverLetter/PhotoCoverLetter";
import SimpleBlushCoverLetter from "../../Templates/CoverLetter/SimpleBlushCoverLetter";
import ModernSlateCoverLetter from "../../Templates/CoverLetter/ModernSlateCoverLetter";
import ProfessionalBlueCoverLetter from "../../Templates/CoverLetter/ProfessionalBlueCoverLetter";
import ExecutiveTemplate from "../../Templates/Executive";
import ProfessionalBlueTemplate from "../../Templates/ProfessionalBlue";
import PhotoTemplate from "../../Templates/PhotoTemplate";
import ProfessionalTemplate from "../../Templates/Professional";
import SimpleBlushTemplate from "../../Templates/SimpleBlush";
import ModernSlateTemplate from "../../Templates/ModernSlate";
import ObsidianEdgeTemplate from "../../Templates/ObsidianEdge";
import CleanProfessionalTemplate from "../../Templates/CleanProfessional";
import ObsidianEdgeCoverLetter from "../../Templates/CoverLetter/ObsidianEdgeCoverLetter";
import CleanProfessionalCoverLetter from "../../Templates/CoverLetter/CleanProfessionalCoverLetter";
import MinimalistProTemplate from "../../Templates/MinimalistPro";
import MinimalistProCoverLetter from "../../Templates/CoverLetter/MinimalistProCoverLetter";
import PhotoClassicTemplate from "../../Templates/PhotoClassic";
import PhotoClassicCoverLetter from "../../Templates/CoverLetter/PhotoClassicCoverLetter";
import ExecutiveSidebarTemplate from "../../Templates/ExecutiveSidebar";
import ExecutiveSidebarCoverLetter from "../../Templates/CoverLetter/ExecutiveSidebarCoverLetter";
import TraditionalTemplate from "../../Templates/Traditional";
import ATSPrimeTemplate from "../../Templates/ATSPrime";
import SimpleATSTemplate from "../../Templates/SimpleATS";
import TraditionalCoverLetter from "../../Templates/CoverLetter/TraditionalCoverLetter";
import ATSPrimeCoverLetter from "../../Templates/CoverLetter/ATSPrimeCoverLetter";
import SimpleATSCoverLetter from "../../Templates/CoverLetter/SimpleATSCoverLetter";
import PrintableResume from "./PrintableResume";

const { Content } = Layout;
const { Title, Text } = Typography;

// Map template IDs to react-pdf components
const TEMPLATE_MAPPING = {
  classic: ClassicATSTemplate,
  modern: ProfessionalTemplate,
  creative: ExecutiveTemplate,
  photo: PhotoTemplate,
  simple_blush: SimpleBlushTemplate,
  modern_slate: ModernSlateTemplate,
  professional_blue: ProfessionalBlueTemplate,
  obsidian_edge: ObsidianEdgeTemplate,
  clean_professional: CleanProfessionalTemplate,
  minimalist_pro: MinimalistProTemplate,
  photo_classic: PhotoClassicTemplate,
  executive_sidebar: ExecutiveSidebarTemplate,
  traditional: TraditionalTemplate,
  ats_prime: ATSPrimeTemplate,
  simple_ats: SimpleATSTemplate,
};

// Map cover letter template IDs to react-pdf components
const COVER_LETTER_TEMPLATE_MAPPING = {
  classic: ClassicCoverLetter,
  modern: ModernCoverLetter,
  creative: CreativeCoverLetter,
  photo: PhotoCoverLetter,
  simple_blush: SimpleBlushCoverLetter,
  modern_slate: ModernSlateCoverLetter,
  professional_blue: ProfessionalBlueCoverLetter,
  obsidian_edge: ObsidianEdgeCoverLetter,
  clean_professional: CleanProfessionalCoverLetter,
  minimalist_pro: MinimalistProCoverLetter,
  photo_classic: PhotoClassicCoverLetter,
  executive_sidebar: ExecutiveSidebarCoverLetter,
  traditional: TraditionalCoverLetter,
  ats_prime: ATSPrimeCoverLetter,
  simple_ats: SimpleATSCoverLetter,
};

// Template Preloader Component - generates PDFs for all templates in background
const TemplatePreloader = ({ resumeData, onTemplateReady, templatesLoading }) => {
  const templates = ["classic", "modern", "creative", "photo", "simple_blush", "modern_slate", "professional_blue", "obsidian_edge", "clean_professional", "minimalist_pro", "photo_classic", "executive_sidebar", "traditional", "ats_prime", "simple_ats"];

  if (!resumeData) return null;

  return (
    <div style={{ display: "none" }}>
      {templates.map((templateId) => {
        const TemplateComponent = TEMPLATE_MAPPING[templateId];
        const pdfData = prepareResumeDataForPDF(resumeData);

        if (!pdfData) return null;

        return (
          <BlobProvider
            key={templateId}
            document={<TemplateComponent resumeData={pdfData} customStyles={{}} customSectionOrder={null} />}
          >
            {({ url, loading, error }) => {
              useEffect(() => {
                if (url && !loading && !error) {
                  onTemplateReady(templateId, url);
                }
                if (!loading) {
                  // Update loading state even if there's an error
                  onTemplateReady(templateId, url, error);
                }
              }, [url, loading, error]);

              return null;
            }}
          </BlobProvider>
        );
      })}
    </div>
  );
};

// Template Card Component
const TemplateCard = ({ template, onPreview, resumeData, isLoading }) => {
  const [isHovered, setIsHovered] = useState(false);

  const renderPreviewImage = () => {
    // Map template IDs to their corresponding imported WebP images
    const templateImages = {
      classic: ClassicTemplateImage,
      modern: ModernTemplateImage,
      creative: CreativeTemplateImage,
      photo: PhotoTemplateImage,
      simple_blush: SimpleBlushTemplateImage,
      modern_slate: ModernSlateTemplateImage,
      professional_blue: ProfessionalBlueTemplateImage,
      obsidian_edge: ObsidianEdgeTemplateImage,
      clean_professional: CleanProfessionalTemplateImage,
      minimalist_pro: MinimalistProTemplateImage,
      photo_classic: PhotoClassicTemplateImage,
      executive_sidebar: ExecutiveSidebarTemplateImage,
      traditional: TraditionalTemplateImage,
      ats_prime: ATSPrimeTemplateImage,
      simple_ats: SimpleATSTemplateImage,
    };

    const imageSrc = templateImages[template.id] || ClassicTemplateImage;

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          backgroundImage: `url(${imageSrc})`,
          backgroundSize: "100% auto",
          backgroundPosition: "top center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#f9f9f9",
          borderRadius: "4px",
        }}
      />
    );
  };

  return (
    <Card
      hoverable
      bodyStyle={{ padding: 0 }}
      style={{
        cursor: "pointer",
        border: "1px solid #d9d9d9",
        backgroundColor: "white",
        transition: "all 0.3s ease",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        onPreview(template);
      }}
    >
      <div
        style={{
          height: "450px",
          borderRadius: "4px",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        {renderPreviewImage()}
        {(isHovered || isLoading) && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
            }}
          >
            {isLoading ? (
              <div style={{ textAlign: "center" }}>
                <Skeleton.Button active style={{ width: "150px", height: "40px" }} />
                <div style={{ marginTop: "8px", color: "#666", fontSize: "12px" }}>Preparing template...</div>
              </div>
            ) : (
              <Button
                type="primary"
                size="large"
                style={{
                  backgroundColor: "#1890ff",
                  borderColor: "#1890ff",
                  fontSize: "16px",
                  padding: "8px 24px",
                  height: "auto",
                  boxShadow: "0 4px 12px rgba(24, 144, 255, 0.3)",
                }}
              >
                Use this template
              </Button>
            )}
          </div>
        )}
      </div>
      <div
        style={{
          padding: "12px 16px",
          textAlign: "center",
          borderTop: "1px solid #f0f0f0",
          backgroundColor: "white",
        }}
      >
        <Text
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#262626",
            margin: 0,
          }}
        >
          {template.name}
        </Text>
      </div>
    </Card>
  );
};

// Main ResumePreview Component
const ResumePreview = () => {
  const { resumeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fetch = useAuthenticatedFetch();
  const { isAuthenticated } = useAuth();

  // Get template from URL parameter
  const searchParams = new URLSearchParams(location.search);
  const templateFromUrl = searchParams.get("template");
  const shareModalFromUrl = searchParams.has("share");

  const [loading, setLoading] = useState(true);
  const [resumeData, setResumeData] = useState(null);
  const [error, setError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(templateFromUrl || null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showShareRequired, setShowShareRequired] = useState(shareModalFromUrl);
  const [viewMode, setViewMode] = useState("resume"); // 'resume' or 'coverLetter'
  const [hasShared, setHasShared] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [linkedInClicked, setLinkedInClicked] = useState(false);
  const [sharedUrl, setSharedUrl] = useState("");
  const [urlSubmitted, setUrlSubmitted] = useState(false);

  // Card-specific sharing state
  const [cardLinkedInClicked, setCardLinkedInClicked] = useState({});
  const [cardSharedUrl, setCardSharedUrl] = useState({});

  // Shareable cards state
  const [selectedCard, setSelectedCard] = useState(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const cardRef = useRef();
  const fullSizeCardRef = useRef();

  // Card description templates with auto-generated resume link
  const getCardDescription = (cardId) => {
    const publicResumeLink = resumeData?.publicSlug
      ? `${import.meta.env.VITE_SITE_URL || "https://www.upresume.ai"}/r/${resumeData.publicSlug}`
      : `${import.meta.env.VITE_SITE_URL || "https://www.upresume.ai"}`;

    // Unique descriptions for each card
    const descriptions = {
      1: `🚀 Excited to share my career journey avatar! Looking for new opportunities where I can bring my skills and passion to make a real impact.

📊 Check out my professional avatar highlights and let's connect!

View my full resume avatar: ${publicResumeLink}

#OpenToWork #CareerGrowth #JobSearch #Hiring #NewOpportunities #ProfessionalDevelopment #Avatar`,

      2: `🎯 Just like Spotify Wrapped, here's my career avatar wrapped! I'm actively seeking my next challenge where I can contribute, grow, and collaborate with amazing teams.

🎵 Let's create something extraordinary together with my professional avatar!

Explore my avatar journey: ${publicResumeLink}

#OpenToWork #CareerJourney #JobHunt #Opportunities #Innovation #TeamWork #Avatar`,

      3: `🎬 My professional avatar story, Netflix style! Currently open to new opportunities and exciting projects that align with my expertise and aspirations.

🌟 Ready to bring my A-game avatar to the right team!

Discover my avatar experience: ${publicResumeLink}

#OpenToWork #CareerOpportunities #Hiring #JobSearch #ProfessionalGrowth #NextChapter #Avatar`,

      4: `💻 My professional avatar contributions, GitHub style! Actively looking for opportunities to contribute to impactful projects and collaborate with talented professionals.

⭐ Let's build something great together with my coding avatar!

See my full avatar profile: ${publicResumeLink}

#OpenToWork #TechCareers #JobOpportunities #Hiring #Collaboration #Innovation #Avatar`,

      5: `🎵 My career avatar playlist, Spotify Desktop edition! Currently tuning into new career frequencies and seeking opportunities that resonate with my professional rhythm.

🎧 Let's make beautiful work together with my professional avatar!

Stream my avatar experience: ${publicResumeLink}

#OpenToWork #CareerMoves #JobSearch #Hiring #ProfessionalPlaylist #NextGig #Avatar`,

      6: `📸 My career avatar story, Instagram style! Swipe through my professional journey and see why I'm the perfect addition to your team.

❤️ Let's create amazing things together with my professional avatar!

View my full resume avatar: ${publicResumeLink}

#OpenToWork #CareerStory #JobOpportunities #Hiring #ProfessionalGrowth #InstaCareer #Avatar`,

      7: `🦉 Level up your career search! My professional avatar journey, Duolingo style! I've been building my skill streak and I'm ready to apply it to exciting new challenges.

🔥 Maintaining my work streak and seeking opportunities to level up my avatar!

Continue my avatar journey: ${publicResumeLink}

#OpenToWork #CareerGrowth #JobSearch #SkillMastery #ProfessionalDevelopment #LevelUp #Avatar`,

      8: `🎵 My Complete 2024 Avatar Wrapped! Everything from my professional journey this year on one beautiful avatar card - profile, stats, skills, personality, and how to reach me.

🌟 All the avatar highlights, zero scrolling needed!

View my full resume avatar: ${publicResumeLink}

#OpenToWork #SpotifyWrapped #CareerSummary #2024Wrapped #JobSearch #ProfessionalProfile #AllInOne #Avatar`,

      9: `🎵 Now Playing: Your Next Hire Avatar! Apple Music-style showcase of my professional journey. Hit play on my career avatar playlist featuring top skills, proven experience, and ready-to-ship code.

🔴 Available for immediate streaming (hiring) my professional avatar!

Listen to my avatar story: ${publicResumeLink}

#OpenToWork #AppleMusic #NowPlaying #CareerPlaylist #HireMe #TechTalent #TopDeveloper #Avatar`,

      10: `💬 My Discord Avatar Server Profile! Level up your team with my professional avatar expertise. Active contributor, proven communicator, and ready to join your amazing community.

🎮 Currently online and seeking new guild opportunities with my professional avatar!

Join my avatar server: ${publicResumeLink}

#OpenToWork #Discord #TeamPlayer #TechCommunity #Collaboration #Hiring #RemoteWork #Avatar`,

      11: `💻 My GitHub Avatar Profile, now your next hire! Full-stack developer avatar with ${
        publicResumeLink.includes("upresume") ? "1,337" : "significant"
      } contributions and a proven track record of building impactful projects.

🌟 Check out my pinned repositories and avatar contribution graph!

⭐ Open to new opportunities and exciting collaborations with my coding avatar.

Explore my avatar profile: ${publicResumeLink}

#OpenToWork #GitHub #Developer #FullStack #OpenSource #Hiring #TechJobs #Coding #Avatar`,

      12: `🎙️ New Episode Alert: Your Next Hire Avatar! Spotify Podcasts-style showcase featuring my professional avatar journey, key achievements, and what makes me the perfect fit for your team.

🔴 Subscribe for career avatar updates and let's start the conversation!

Listen to my avatar now: ${publicResumeLink}

#OpenToWork #SpotifyPodcasts #CareerPodcast #Hiring #ProfessionalStory #NextEpisode #Avatar`,

      13: `⭐ Rated 9.5/10 - IMDb Professional Avatar Profile! My career filmography showcases award-winning projects, stellar reviews, and a proven track record of blockbuster results.

🏆 Critics (employers) agree: This is a must-hire avatar talent!

See full avatar credits: ${publicResumeLink}

#OpenToWork #IMDb #CareerHighlights #TopRated #ProfessionalProfile #AwardWinning #Hiring #Avatar`,

      14: `📊 Channel Avatar Dashboard Analytics - My career is performing at its peak! YouTube Studio-style metrics show consistent growth in projects, skills mastery, and measurable impact.

📈 Key Avatar Performance Indicators:
• Projects: ↑156% growth
• Skills: ↑45% mastered
• Impact: ↑380% value generated

🔴 Subscribe to my professional avatar journey: ${publicResumeLink}

#OpenToWork #YouTubeStudio #Analytics #CareerGrowth #DataDriven #HighPerformer #Hiring #TechTalent #Avatar`,
    };

    return descriptions[cardId] || descriptions[1];
  };

  const handleCopyDescription = (cardId) => {
    const description = getCardDescription(cardId);
    navigator.clipboard
      .writeText(description)
      .then(() => {
        message.success("Description copied to clipboard!");
      })
      .catch(() => {
        message.error("Failed to copy description");
      });
  };

  // Customization state - store at parent level for access during downloads
  const [currentCustomizations, setCurrentCustomizations] = useState({});
  const [currentSectionOrder, setCurrentSectionOrder] = useState(null);

  // Template preloading state
  const [preloadedTemplates, setPreloadedTemplates] = useState({});
  const [templatesLoading, setTemplatesLoading] = useState({
    classic: true,
    modern: true,
    creative: true,
    photo: true,
    traditional: true,
    ats_prime: true,
    simple_ats: true,
  });

  const printableResumeRef = useRef();
  const customizableResumeRef = useRef();

  const templates = [
    {
      id: "classic",
      name: "Classic",
      description: "Traditional and professional design perfect for corporate positions",
      features: ["Clean Layout", "Professional", "ATS-Friendly", "Traditional"],
    },
    {
      id: "modern",
      name: "Modern",
      description: "Contemporary design with accent colors and modern typography",
      features: ["Colorful", "Two-Column", "Modern Typography", "Eye-Catching"],
    },
    {
      id: "creative",
      name: "Creative",
      description: "Unique and creative design perfect for creative industries",
      features: ["Creative Layout", "Gradients", "Unique Design", "Stand Out"],
    },
    {
      id: "photo",
      name: "Photo",
      description: "Professional design with profile photo and two-column layout",
      features: ["Profile Photo", "Two-Column", "Modern", "Professional"],
    },
    {
      id: "simple_blush",
      name: "Simple Blush",
      description: "Clean, modern design with blush pink accents and skill progress bars",
      features: ["Blush Accents", "Skill Bars", "Two-Column", "Modern"],
    },
    {
      id: "modern_slate",
      name: "Modern Slate",
      description: "Professional slate design with profile photo and two-column layout",
      features: ["Profile Photo", "Slate Header", "Two-Column", "Professional"],
    },
    {
      id: "professional_blue",
      name: "Professional Blue",
      description: "Elegant design with dark blue header, centered name, and decorative accents",
      features: ["Blue Header", "Centered Layout", "Decorative", "Professional"],
    },
    {
      id: "obsidian_edge",
      name: "Obsidian Edge",
      description: "Bold dark header with professional contrast and icon-based section headers",
      features: ["Dark Header", "Icon Headers", "Professional Contrast", "Modern"],
    },
    {
      id: "clean_professional",
      name: "Clean Professional",
      description: "Minimalist two-column design with section labels on left and content on right",
      features: ["Two-Column", "Minimalist", "Clean Layout", "Professional"],
    },
    {
      id: "minimalist_pro",
      name: "Minimalist Pro",
      description: "Clean modern design with teal accent bar and elegant pill-style skills",
      features: ["Teal Accent", "Pill Skills", "Modern", "Clean Layout"],
    },
    {
      id: "photo_classic",
      name: "Photo Classic",
      description: "Clean professional design with profile photo and date-aligned sections",
      features: ["Profile Photo", "Date Sidebar", "Multi-Column Skills", "References"],
    },
    {
      id: "executive_sidebar",
      name: "Executive Sidebar",
      description: "Professional two-column layout with dark blue sidebar and circular profile photo",
      features: ["Profile Photo", "Sidebar Layout", "Skill Progress Bars", "Modern"],
    },
    {
      id: "traditional",
      name: "Traditional",
      description: "Classic professional design with centered headers, bordered sections, and dotted skill lines",
      features: ["Centered Headers", "Bordered Sections", "Dotted Skills", "Traditional"],
    },
    {
      id: "ats_prime",
      name: "ATS Prime",
      description: "ATS-optimized design with blue accents, double-line dividers, and 4-column skill grid",
      features: ["ATS-Optimized", "Blue Accents", "Double Dividers", "4-Column Skills"],
    },
    {
      id: "simple_ats",
      name: "Simple ATS",
      description: "Clean modern design with blue headers, bullet-point skills, and split header layout",
      features: ["Blue Headers", "Bullet Skills", "Split Header", "ATS-Friendly"],
    },
  ];

  useEffect(() => {
    if (resumeId) {
      fetchResumeData();
    }

    // Preload WhatsApp icon to avoid loading delay
    const img = new Image();
    img.src = Whatsapp;

    // Track incoming traffic from shared URLs
    const urlParams = new URLSearchParams(window.location.search);
    const refParam = urlParams.get("ref");

    if (refParam && typeof gtag !== "undefined") {
      // Platform-specific traffic events based on ref parameter
      const platformMap = {
        ln: "linkedin_traffic", // ln1224 = LinkedIn December 2024
        tw: "twitter_traffic", // tw1224 = Twitter December 2024
        fb: "facebook_traffic", // fb1224 = Facebook December 2024
        rd: "reddit_traffic", // rd1224 = Reddit December 2024
      };

      const platformCode = refParam.substring(0, 2); // Extract first 2 characters
      const eventName = platformMap[platformCode];

      if (eventName) {
        gtag("event", eventName, {
          ref_code: refParam,
          traffic_source: "social_share",
          page_location: window.location.href,
          value: 1,
        });

        logger.log(`Tracked incoming traffic: ${eventName} with ref=${refParam}`, {
          extras: {
            eventName,
            refParam,
            context: "traffic_tracking",
          },
        });
      }
    }
  }, [resumeId]);

  // Update selectedTemplate when URL changes
  useEffect(() => {
    if (templateFromUrl && templates.find((t) => t.id === templateFromUrl)) {
      setSelectedTemplate(templateFromUrl);
    }
  }, [templateFromUrl]);

  // Check if user has shared based on database
  useEffect(() => {
    if (shareModalFromUrl) {
      // User navigated with ?share parameter
      // Check if they already shared before (from database)
      if (resumeData?.sharedUrl) {
        setHasShared(true);
        setShowShareRequired(true); // Still show share content but with download button
      } else if (resumeData !== null) {
        // Only show share requirement if resume data is loaded and no sharedUrl
        setShowShareRequired(true);
        setHasShared(false);
      }
    } else {
      // Normal preview, don't show share content
      setShowShareRequired(false);
    }
  }, [shareModalFromUrl, resumeId, resumeData]);

  // Handle template preloading completion
  const handleTemplateReady = (templateId, url, error) => {
    setTemplatesLoading((prev) => ({
      ...prev,
      [templateId]: false,
    }));

    if (url && !error) {
      setPreloadedTemplates((prev) => ({
        ...prev,
        [templateId]: url,
      }));
    } else if (error) {
      logger.error(`Error preloading template ${templateId}`, {
        extras: {
          templateId,
          error: error.message || error,
          context: "template_preloading",
        },
      });
    }
  };

  const fetchResumeData = async () => {
    try {
      const response = await fetch.get(`/resume/${resumeId}`);
      if (!response.success) {
        throw new Error("Failed to fetch resume data");
      }
      setResumeData(response.data);

      // Set profile photo URL if exists
      if (response.data?.profilePhoto?.url) {
        setProfilePhotoUrl(response.data.profilePhoto.url);
      }

      // Check if resume has already been shared (from database sharedUrl)
      if (response.data?.sharedUrl) {
        setHasShared(true);
      } else {
        // console.log("No sharedUrl found, user needs to share");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    // Update URL with template parameter
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("template", template.id);
    navigate(`/app/resume/${resumeId}/preview?${searchParams.toString()}`, { replace: true });
    setSelectedTemplate(template.id);
  };

  const handleBackToTemplates = () => {
    // Remove template and share parameters from URL
    navigate(`/app/resume/${resumeId}/preview`, { replace: true });
    setSelectedTemplate(null);
    setShowShareRequired(false);
    setHasShared(false);
  };

  const handlePrintFromPreview = async () => {
    if (!selectedTemplate) return;
    const currentTemplate = templates.find((t) => t.id === selectedTemplate);

    // Close modal immediately
    setPreviewModalVisible(false);

    // Use the CustomizableResumePreview's print method if available
    if (customizableResumeRef.current) {
      try {
        customizableResumeRef.current.handlePrint();
      } catch (err) {
        logger.error("CustomizableResumePreview print error", {
          extras: {
            error: err.message || err,
            context: "resume_preview_print",
          },
        });
        message.error("Print failed. Please try again.");
      }
      return;
    }

    // Fallback to old print method if ref not available

    try {
      // Create a hidden iframe for silent printing/PDF generation
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.top = "-10000px";
      iframe.style.left = "-10000px";
      iframe.style.width = "8.5in";
      iframe.style.height = "11in";
      document.body.appendChild(iframe);

      // Wait for DOM to be fully updated
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Get the resume content with print CSS
      const resumeContent = document.querySelector(".printable-resume");
      if (!resumeContent) {
        throw new Error("Resume content not found");
      }

      // Clone the resume content
      const clonedContent = resumeContent.cloneNode(true);

      // Calculate content density for dynamic spacing
      const calculateContentDensity = (data) => {
        const totalItems =
          (data?.experience?.length || 0) +
          (data?.education?.length || 0) +
          (data?.projects?.length || 0) +
          (data?.certifications?.length || 0) +
          (data?.achievements?.length || 0) +
          (data?.skills ? Object.keys(data.skills).length : 0);

        const totalText = [
          data?.summary || "",
          data?.objective || "",
          ...(data?.experience || []).map((exp) => (exp.description || "").length),
          ...(data?.projects || []).map((proj) => (proj.description || "").length),
        ].join("").length;

        // Determine density based on total items and text length
        if (totalItems >= 15 || totalText > 2000) return "very-high";
        if (totalItems >= 10 || totalText > 1500) return "high";
        if (totalItems >= 6 || totalText > 1000) return "medium";
        return "low";
      };

      const contentDensity = calculateContentDensity(sortedData);

      // Add content density attribute to printable-resume div
      const printableResumeDiv = clonedContent.querySelector(".printable-resume");
      if (printableResumeDiv) {
        printableResumeDiv.setAttribute("data-content-density", contentDensity);
      }

      // Create complete HTML document with print CSS for the iframe
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Resume - ${currentTemplate.name}</title>
          <style>
            @media print, screen {
              * { box-sizing: border-box; margin: 0; padding: 0; }
              
              .printable-resume {
                font-family: ${
                  currentTemplate.id === "classic"
                    ? "Times, serif"
                    : currentTemplate.id === "modern"
                    ? "Arial, sans-serif"
                    : "Georgia, serif"
                };
                max-width: 8.5in;
                width: 100%;
                margin: 0 auto;
                padding: 0.5in;
                background: white;
                color: black;
                line-height: 1.2;
                font-size: 10pt;
                box-sizing: border-box;
                min-height: 10.5in;
                overflow: visible;
              }

              .header {
                text-align: center;
                margin-bottom: 6pt;
                border-bottom: ${
                  currentTemplate.id === "classic"
                    ? "1pt solid #333"
                    : currentTemplate.id === "modern"
                    ? "2pt solid #2563eb"
                    : "1pt solid #667eea"
                };
                padding-bottom: 2pt;
              }

              .header h1 {
                font-size: ${currentTemplate.id === "modern" ? "16pt" : "14pt"};
                font-weight: bold;
                margin: 0 0 4pt 0;
                color: ${currentTemplate.id === "modern" ? "#2563eb" : "black"};
              }

              .header .contact {
                font-size: 9pt;
                color: #666;
                margin: 0;
              }

              .section {
                margin-bottom: 4pt;
                page-break-inside: avoid;
              }

              .section-title {
                font-size: 10pt;
                font-weight: bold;
                text-transform: uppercase;
                border-bottom: 1pt solid #333;
                padding-bottom: 1pt;
                margin-bottom: 3pt;
                color: black;
              }

              .section-content {
                font-size: 9pt;
                line-height: 1.1;
              }

              .experience-item, .education-item {
                margin-bottom: 3pt;
                page-break-inside: avoid;
              }

              .experience-title, .education-title {
                font-size: 9pt;
                font-weight: bold;
                margin: 0 0 1pt 0;
                color: black;
              }

              .experience-company, .education-school {
                font-size: 9pt;
                color: black;
                margin: 0 0 1pt 0;
              }

              .experience-date, .education-date {
                font-size: 9pt;
                color: #666;
                margin: 0 0 3pt 0;
              }

              .experience-description {
                font-size: 8pt;
                line-height: 1.2;
                margin: 0;
                color: black;
              }

              .education-gpa {
                font-size: 9pt;
                color: #666;
                margin: 1pt 0 0 0;
              }

              .skills-list {
                font-size: 9pt;
                line-height: 1.1;
                margin: 0;
                display: grid !important;
                grid-template-columns: 1fr 1fr 1fr !important;
                gap: 2pt 6pt !important;
                column-gap: 6pt !important;
              }
              
              .skill-item {
                margin: 0;
                padding: 0;
                display: flex;
                align-items: flex-start;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                font-size: 9pt;
                line-height: 1.1;
              }
              
              .skill-item:before {
                content: "•";
                margin-right: 3pt;
                color: black;
                flex-shrink: 0;
              }

              /* Modern template specific styles */
              .modern-header {
                background: #2563eb !important;
                color: white !important;
                margin: -0.5in -0.5in 3pt -0.5in !important;
                padding: 4pt 0.5in !important;
                text-align: center !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              .modern-header h1 {
                color: white !important;
                font-size: 14pt !important;
                font-weight: bold !important;
                margin: 0 0 2pt 0 !important;
              }

              .modern-header .contact {
                color: rgba(255, 255, 255, 0.9) !important;
                font-size: 9pt !important;
              }

              .modern .section-title {
                color: #2563eb !important;
                border-left: 3pt solid #2563eb !important;
                border-bottom: none !important;
                padding-left: 6pt !important;
                padding-bottom: 0 !important;
                font-size: 10pt !important;
                font-weight: bold !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              /* Creative template specific styles */
              .creative-header {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
                background-color: #667eea !important;
                color: white !important;
                margin: -0.5in -0.5in 3pt -0.5in !important;
                padding: 4pt 0.5in !important;
                text-align: center !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              .creative-header h1 {
                color: white !important;
                font-size: 14pt !important;
                font-weight: bold !important;
                margin: 0 0 2pt 0 !important;
              }

              .creative-header .contact {
                color: rgba(255, 255, 255, 0.9) !important;
                font-size: 9pt !important;
              }

              .creative .section-title {
                color: #667eea !important;
                border-bottom: 1pt solid #667eea !important;
                font-size: 10pt !important;
                font-weight: bold !important;
                padding-bottom: 1pt !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }

              @page {
                margin: 0.5in;
                size: letter;
              }

              html, body {
                height: auto;
                width: 100%;
                margin: 0;
                padding: 0;
                overflow: visible;
              }
            }
          </style>
        </head>
        <body>
          ${clonedContent.outerHTML}
        </body>
        </html>
      `;

      // Write content to iframe
      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();

      // Wait for content to load, then trigger print with user confirmation
      setTimeout(() => {
        try {
          // Conservative approach - no navigation to avoid false positives
          let timeoutId;

          // Cleanup function
          const cleanup = () => {
            if (timeoutId) clearTimeout(timeoutId);

            // Clean up iframe
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          };

          // Simple event handler - just cleanup, no navigation
          const afterPrintHandler = () => {
            // Remove event listener
            iframe.contentWindow.removeEventListener("afterprint", afterPrintHandler);

            // Cleanup after a short delay
            setTimeout(() => {
              cleanup();
            }, 500);
          };

          // Set up single event listener
          iframe.contentWindow.addEventListener("afterprint", afterPrintHandler);

          // Safety timeout - always cleanup
          timeoutId = setTimeout(() => {
            iframe.contentWindow.removeEventListener("afterprint", afterPrintHandler);
            cleanup();
          }, 30000);

          // Focus iframe and trigger print
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (printErr) {
          logger.error("Print error", {
            extras: {
              error: printErr.message || printErr,
              context: "resume_print_error",
            },
          });
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          throw printErr;
        }
      }, 1000);
    } catch (err) {
      logger.error("Print error", {
        extras: {
          error: err.message || err,
          context: "general_print_error",
        },
      });
      message.error("Print failed. Please try again.");
    }
  };

  const handleDownloadClick = () => {
    // Check if user is authenticated - if not, redirect to login
    const authToken = localStorage.getItem("authorization");
    const adminToken = sessionStorage.getItem("adminResumeAccessToken");
    if (!authToken && !adminToken && !isAuthenticated) {
      // Redirect to login, after which user will go to resume-choice page
      const returnUrl = encodeURIComponent("/app/resume-choice");
      navigate(`/app/login?returnUrl=${returnUrl}`);
      return;
    }

    // User is authenticated - show share content with template parameter
    const currentParams = new URLSearchParams(location.search);
    currentParams.set("share", "");
    // Keep the template parameter if it exists
    if (selectedTemplate) {
      currentParams.set("template", selectedTemplate);
    }
    navigate(`/app/resume/${resumeId}/preview?${currentParams.toString()}`, { replace: true });
    setShowShareRequired(true);
  };

  // Dual Download Handler - Downloads both Resume and Cover Letter
  const handleDownloadBoth = async () => {
    if (!selectedTemplate) {
      message.error("Please select a template first");
      return;
    }

    setDownloadLoading(true);

    try {
      const personalInfo = resumeData?.parsedData?.personalInfo || {};
      const fileName = personalInfo.name || "Document";

      // Download Resume
      const ResumeTemplate = TEMPLATE_MAPPING[selectedTemplate];
      const resumePdfData = prepareResumeDataForPDF(resumeData);
      if (resumePdfData && ResumeTemplate) {
        const resumeBlob = await pdf(
          <ResumeTemplate resumeData={resumePdfData} customStyles={{}} customSectionOrder={null} />
        ).toBlob();
        const resumeUrl = URL.createObjectURL(resumeBlob);
        const resumeLink = document.createElement("a");
        resumeLink.href = resumeUrl;
        resumeLink.download = `Resume_${fileName}.pdf`;
        resumeLink.click();
        URL.revokeObjectURL(resumeUrl);
      }

      // Download Cover Letter if exists
      if (resumeData?.coverLetter?.generatedContent) {
        const CoverLetterTemplate = COVER_LETTER_TEMPLATE_MAPPING[selectedTemplate];
        if (CoverLetterTemplate) {
          // Small delay to avoid browser blocking multiple downloads
          await new Promise((resolve) => setTimeout(resolve, 500));

          const coverLetterBlob = await pdf(
            <CoverLetterTemplate resumeData={resumeData} coverLetterData={resumeData.coverLetter} />
          ).toBlob();
          const coverLetterUrl = URL.createObjectURL(coverLetterBlob);
          const coverLetterLink = document.createElement("a");
          coverLetterLink.href = coverLetterUrl;
          coverLetterLink.download = `CoverLetter_${fileName}.pdf`;
          coverLetterLink.click();
          URL.revokeObjectURL(coverLetterUrl);

          message.success("Resume and Cover Letter downloaded!");
        } else {
          message.success("Resume downloaded!");
        }
      } else {
        message.success("Resume downloaded!");
      }

      // Navigate to thank you page
      setTimeout(() => {
        navigate(`/app/resume/${resumeId}/thank-you`);
      }, 1000);
    } catch (error) {
      logger.error("Download failed", {
        extras: {
          error: error.message || error,
          context: "dual_download_error",
        },
      });
      message.error("Download failed. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleCloseShareModal = () => {
    // Close sharing modal and remove share parameter from URL
    const currentParams = new URLSearchParams(location.search);
    currentParams.delete("share");
    const newUrl = currentParams.toString()
      ? `/app/resume/${resumeId}/preview?${currentParams.toString()}`
      : `/app/resume/${resumeId}/preview`;
    navigate(newUrl, { replace: true });
    setShowShareRequired(false);
  };

  const handleDownloadFromPreview = () => {
    // This function is now replaced by the PDFDownloadLink component
    // The actual download is handled by react-pdf
    message.success("Resume downloaded successfully!");

    // Keep sharing session active - don't clear it
    // User should be able to download multiple templates without sharing again

    // Navigate to thank you page
    setTimeout(() => {
      navigate(`/app/resume/${resumeId}/thank-you`);
    }, 1000);
  };

  const handleBackToChat = () => {
    navigate(`/app/resume/${resumeId}`);
  };

  const handleLinkedInShare = async () => {
    // Save share status to database (only if not already shared)
    if (!hasShared) {
      try {
        await fetch.post(`/resume/${resumeId}/track-share`, {
          platform: "linkedin",
        });
      } catch (error) {
        logger.error("Failed to save share status", {
          extras: {
            error: error.message || error,
            platform: "linkedin",
            context: "linkedin_share_tracking",
          },
        });
      }
    }
    // Generate tracking URL with UTM parameters for LinkedIn
    const generateLinkedInTrackingURL = () => {
      const baseURL = import.meta.env.VITE_SITE_URL || "https://www.upresume.ai/";
      const date = new Date();
      const mmyy = `${(date.getMonth() + 1).toString().padStart(2, "0")}${date
        .getFullYear()
        .toString()
        .slice(-2)}`;
      const utmParams = {
        utm_source: "linkedin",
        utm_medium: "social_share",
        ref: `ln${mmyy}`,
      };

      const queryString = new URLSearchParams(utmParams).toString();
      return `${baseURL}?${queryString}`;
    };

    const trackingURL = generateLinkedInTrackingURL();
    const shareText = `Recently I updated my resume in under 10 minutes — for FREE.
No sign-ups. No paywalls. Just results. 🚀

I've tried a lot of resume tools before, but this one honestly felt different. Here's why:
✅ AI analysis that shows strengths & areas for improvement
✅ Helpful tips every time you enhance a section — and they actually get better as you go
✅ Freedom to edit any field manually, so I stayed in control
✅ Clean, ATS-friendly templates ready to download instantly
✅ And yes — all of this without even creating an account

For me, the best part was seeing exactly what needed improvement and being able to fix it right away. It felt like having a career coach built into the tool.

💼 If you're preparing for job applications or just want to polish your CV, I highly recommend giving it a try.
👉 ${trackingURL}

#JobSearch #CareerAdvice #InterviewTips #Hiring #Jobs #Careers #JobInterviews #ResumeTips #ResumeWriting #JobSeekers #CareerGrowth #JobHunting #AItools #FutureOfWork #OpenToWork`;

    // Track share initiation in Google Analytics
    if (typeof gtag !== "undefined") {
      gtag("event", import.meta.env.VITE_GA_SOCIAL_SHARE_EVENT || "social_share", {
        social_platform: "linkedin",
        linkedin_type: "personal",
        value: 1,
      });
    }

    const linkedInUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(shareText)}`;
    window.open(linkedInUrl, "_blank", "width=600,height=400");

    // Show URL input field and submit button after LinkedIn click
    setLinkedInClicked(true);
    message.success("Great! Now please submit the URL where you shared this post to unlock download.");
  };

  // WhatsApp sharing functionality - COMMENTED OUT FOR NOW
  /*
  const handleWhatsAppShare = async () => {
    // Save share status to database (only if not already shared)
    if (!hasShared) {
      try {
        await fetch.post(`/resume/${resumeId}/track-share`, {
          platform: 'whatsapp'
        });
      } catch (error) {
        logger.error('Failed to save share status', {
          extras: {
            error: error.message || error,
            platform: 'whatsapp',
            context: "whatsapp_share_tracking"
          }
        });
      }
    }
    // Generate tracking URL with UTM parameters
    const generateTrackingURL = () => {
      const baseURL = import.meta.env.VITE_SITE_URL || "https://www.upresume.ai/";
      const utmParams = {
        utm_source: 'whatsapp',
        utm_medium: 'social_share',
        utm_campaign: `resume_builder_${new Date().toISOString().slice(0,10)}`
      };

      const queryString = new URLSearchParams(utmParams).toString();
      return `${baseURL}?${queryString}`;
    };

    const trackingURL = generateTrackingURL();
    const shareText = `Hey! Just tried this free AI resume tool and it's actually good :)

No signup needed - just upload and it tells you what's wrong with your CV plus gives templates

Actually worked for me! Worth a try: ${trackingURL}`;

    // Track share initiation in Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', import.meta.env.VITE_GA_SOCIAL_SHARE_EVENT || 'social_share', {
        social_platform: 'whatsapp',
        share_action: 'share_initiated',
        template_type: selectedTemplate,
        resume_id: resumeId,
        share_type: 'button_click',
        user_type: isLoggedIn ? 'registered' : 'anonymous',
        share_location: 'preview',
        value: 1
      });
    }

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, "_blank", "width=600,height=400");

    // Track share completion (best effort - after 3 seconds)
    setTimeout(async () => {
      if (typeof gtag !== 'undefined') {
        gtag('event', import.meta.env.VITE_GA_SOCIAL_SHARE_EVENT || 'social_share', {
          social_platform: 'whatsapp',
          share_action: 'share_completed',
          template_type: selectedTemplate,
          resume_id: resumeId,
          value: 1
        });
      }
    }, 3000);

    // Mark as shared
    setHasShared(true);
    message.success("Thank you for sharing! You can now download your resume.");
  };
  */

  const handleXShare = async () => {
    // Save share status to database (only if not already shared)
    if (!hasShared) {
      try {
        await fetch.post(`/resume/${resumeId}/track-share`, {
          platform: "twitter",
        });
      } catch (error) {
        logger.error("Failed to save share status", {
          extras: {
            error: error.message || error,
            platform: "twitter",
            context: "twitter_share_tracking",
          },
        });
      }
    }

    // Generate tracking URL with UTM parameters for X/Twitter
    const generateXTrackingURL = () => {
      const baseURL = import.meta.env.VITE_SITE_URL || "https://www.upresume.ai/";
      const date = new Date();
      const mmyy = `${(date.getMonth() + 1).toString().padStart(2, "0")}${date
        .getFullYear()
        .toString()
        .slice(-2)}`;
      const utmParams = {
        utm_source: "twitter",
        utm_medium: "social_share",
        ref: `tw${mmyy}`,
      };

      const queryString = new URLSearchParams(utmParams).toString();
      return `${baseURL}?${queryString}`;
    };

    const trackingURL = generateXTrackingURL();
    const shareText = `🚀 Updated my resume in 10min with this FREE AI tool!

✅ No sign-ups or paywalls
✅ AI analysis with improvements
✅ ATS-friendly templates
✅ Complete control over editing

${trackingURL}

#JobSearch #AI #ResumeBuilder #OpenToWork`;

    // Track share initiation in Google Analytics
    if (typeof gtag !== "undefined") {
      gtag("event", import.meta.env.VITE_GA_SOCIAL_SHARE_EVENT || "social_share", {
        social_platform: "twitter",
        value: 1,
      });
    }

    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(twitterUrl, "_blank", "width=600,height=400");

    // Show URL input field instead of immediate unlock
    setShowUrlInput(true);
    message.success("Great! Now please submit the URL where you shared this post to unlock download.");
  };

  const handleFacebookShare = async () => {
    // Save share status to database (only if not already shared)
    if (!hasShared) {
      try {
        await fetch.post(`/resume/${resumeId}/track-share`, {
          platform: "facebook",
        });
      } catch (error) {
        logger.error("Failed to save share status", {
          extras: {
            error: error.message || error,
            platform: "facebook",
            context: "facebook_share_tracking",
          },
        });
      }
    }

    // Generate tracking URL with UTM parameters for Facebook
    const generateFacebookTrackingURL = () => {
      const baseURL = import.meta.env.VITE_SITE_URL || "https://www.upresume.ai/";
      const date = new Date();
      const mmyy = `${(date.getMonth() + 1).toString().padStart(2, "0")}${date
        .getFullYear()
        .toString()
        .slice(-2)}`;
      const utmParams = {
        utm_source: "facebook",
        utm_medium: "social_share",
        ref: `fb${mmyy}`,
      };

      const queryString = new URLSearchParams(utmParams).toString();
      return `${baseURL}?${queryString}`;
    };

    const trackingURL = generateFacebookTrackingURL();

    // Track share initiation in Google Analytics
    if (typeof gtag !== "undefined") {
      gtag("event", import.meta.env.VITE_GA_SOCIAL_SHARE_EVENT || "social_share", {
        social_platform: "facebook",
        value: 1,
      });
    }

    // Facebook Share using the reliable sharer method
    const facebookUrl = `https://www.facebook.com/sharer.php?u=${encodeURIComponent(trackingURL)}`;
    window.open(facebookUrl, "_blank", "width=600,height=500");

    // Show URL input field instead of immediate unlock
    setShowUrlInput(true);
    message.success("Great! Now please submit the URL where you shared this post to unlock download.");
  };

  const handleRedditShare = async () => {
    // Save share status to database (only if not already shared)
    if (!hasShared) {
      try {
        await fetch.post(`/resume/${resumeId}/track-share`, {
          platform: "reddit",
        });
      } catch (error) {
        logger.error("Failed to save share status", {
          extras: {
            error: error.message || error,
            platform: "reddit",
            context: "reddit_share_tracking",
          },
        });
      }
    }

    // Generate tracking URL with UTM parameters for Reddit
    const generateRedditTrackingURL = () => {
      const baseURL = import.meta.env.VITE_SITE_URL || "https://www.upresume.ai/";
      const date = new Date();
      const mmyy = `${(date.getMonth() + 1).toString().padStart(2, "0")}${date
        .getFullYear()
        .toString()
        .slice(-2)}`;
      const utmParams = {
        utm_source: "reddit",
        utm_medium: "social_share",
        ref: `rd${mmyy}`,
      };

      const queryString = new URLSearchParams(utmParams).toString();
      return `${baseURL}?${queryString}`;
    };

    const trackingURL = generateRedditTrackingURL();
    const shareTitle = "Free AI Resume Builder - Updated My Resume in Under 10 Minutes";
    const shareText = `Just used this free AI resume tool and it's genuinely impressive:

🔥 No signup required - just upload and start
🎯 AI analyzes your resume and suggests specific improvements
📋 Professional ATS-friendly templates ready to download
⚡ Takes less than 10 minutes from upload to finished resume

Perfect for anyone job hunting or just wanting to refresh their CV. Actually works unlike most "free" tools that hit you with paywalls.

Worth checking out: ${trackingURL}`;

    // Track share initiation in Google Analytics
    if (typeof gtag !== "undefined") {
      gtag("event", import.meta.env.VITE_GA_SOCIAL_SHARE_EVENT || "social_share", {
        social_platform: "reddit",
        value: 1,
      });
    }

    // Reddit share URL - using selftext parameter for text content
    const redditUrl = `https://www.reddit.com/submit?url=${encodeURIComponent(
      trackingURL
    )}&title=${encodeURIComponent(shareTitle)}&selftext=${encodeURIComponent(shareText)}`;
    window.open(redditUrl, "_blank", "width=600,height=400");

    // Show URL input field instead of immediate unlock
    setShowUrlInput(true);
    message.success("Great! Now please submit the URL where you shared this post to unlock download.");
  };

  const handleUrlSubmit = async () => {
    if (!sharedUrl.trim()) {
      message.error("Please enter the URL where you shared the post.");
      return;
    }

    // Basic URL validation
    try {
      new URL(sharedUrl);
    } catch (error) {
      message.error("Please enter a valid URL.");
      return;
    }

    // Validate if it's from a supported platform
    const supportedPlatforms = ["linkedin.com", "twitter.com", "x.com", "facebook.com", "reddit.com"];

    const isValidPlatform = supportedPlatforms.some((platform) => sharedUrl.toLowerCase().includes(platform));

    if (!isValidPlatform) {
      message.error("Please provide a URL from LinkedIn, Twitter/X, Facebook, or Reddit.");
      return;
    }

    try {
      // Determine platform and platform code
      const detectedPlatform = supportedPlatforms.find((p) => sharedUrl.toLowerCase().includes(p)) || "unknown";
      const platformCodes = {
        "linkedin.com": "ln",
        "twitter.com": "tw",
        "x.com": "tw",
        "facebook.com": "fb",
        "reddit.com": "rd",
      };
      const platformCode = platformCodes[detectedPlatform] || "uk";

      // Save the submitted URL to the database and mark as shared
      await fetch.post(`/resume/${resumeId}/submit-share-url`, {
        sharedUrl: sharedUrl,
        platform: detectedPlatform,
        platformCode: platformCode,
        hasShared: true,
        sharedAt: new Date().toISOString(),
      });

      // Track URL submission in GA4
      if (typeof gtag !== "undefined") {
        gtag("event", "share_url_submitted", {
          platform: detectedPlatform,
          platform_code: platformCode,
          resume_id: resumeId,
          value: 1,
        });
      }

      setUrlSubmitted(true);
      setHasShared(true);
      // Removed sessionStorage - now using database sharedUrl field
      message.success("Thank you! Your shared URL has been verified. You can now download your resume.");
    } catch (error) {
      logger.error("Failed to submit shared URL", {
        extras: {
          error: error.message || error,
          context: "shared_url_submission",
        },
      });
      message.error("Failed to verify your shared URL. Please try again.");
    }
  };

  // Handle card-specific LinkedIn share
  const handleCardLinkedInShare = async (cardId) => {
    // Generate tracking URL with UTM parameters for LinkedIn
    const baseURL = import.meta.env.VITE_SITE_URL || "https://www.upresume.ai/";
    const date = new Date();
    const mmyy = `${(date.getMonth() + 1).toString().padStart(2, "0")}${date.getFullYear().toString().slice(-2)}`;
    const utmParams = {
      utm_source: "linkedin",
      utm_medium: "card_share",
      ref: `ln${mmyy}_card${cardId}`,
    };
    const queryString = new URLSearchParams(utmParams).toString();
    const trackingURL = `${baseURL}?${queryString}`;

    // Get card description and append tracking URL
    const description = getCardDescription(cardId);
    const shareText = `${description}\n\n👉 ${trackingURL}`;

    // Track share initiation in Google Analytics
    if (typeof gtag !== "undefined") {
      gtag("event", import.meta.env.VITE_GA_SOCIAL_SHARE_EVENT || "social_share", {
        social_platform: "linkedin",
        card_id: cardId,
        value: 1,
      });
    }

    const linkedInUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(shareText)}`;
    window.open(linkedInUrl, "_blank", "width=600,height=400");

    // Show URL input field for this specific card only if not already shared
    if (!hasShared) {
      setCardLinkedInClicked((prev) => ({ ...prev, [cardId]: true }));
      message.success("Great! Now please submit the URL where you shared this post to unlock download.");
    } else {
      message.success("LinkedIn share opened! Feel free to share as many times as you like.");
    }
  };

  // Handle card-specific URL submit
  const handleCardUrlSubmit = async (cardId) => {
    const url = cardSharedUrl[cardId];

    if (!url || !url.trim()) {
      message.error("Please enter the URL where you shared the post.");
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch (error) {
      message.error("Please enter a valid URL.");
      return;
    }

    // Validate if it's from LinkedIn
    if (!url.toLowerCase().includes("linkedin.com")) {
      message.error("Please provide a URL from LinkedIn.");
      return;
    }

    try {
      // Save the submitted URL to the database and mark as shared
      await fetch.post(`/resume/${resumeId}/submit-share-url`, {
        sharedUrl: url,
        platform: "linkedin.com",
        platformCode: "ln",
        hasShared: true,
        sharedAt: new Date().toISOString(),
        cardId: cardId,
      });

      // Track URL submission in GA4
      if (typeof gtag !== "undefined") {
        gtag("event", "card_share_url_submitted", {
          platform: "linkedin",
          platform_code: "ln",
          card_id: cardId,
          resume_id: resumeId,
          value: 1,
        });
      }

      setHasShared(true);
      message.success("Thank you! Your shared URL has been verified. You can now download your resume.");
    } catch (error) {
      logger.error("Failed to submit card shared URL", {
        extras: {
          error: error.message || error,
          cardId: cardId,
          context: "card_shared_url_submission",
        },
      });
      message.error("Failed to verify your shared URL. Please try again.");
    }
  };

  const handlePrint = () => {
    // Call the PrintableResume component's handlePrint function
    if (printableResumeRef.current) {
      printableResumeRef.current.handlePrint();
    } else {
      logger.error("printableResumeRef is not available", {
        extras: {
          context: "printable_resume_ref_missing",
        },
      });
    }
  };

  const handleDownloadCard = async (cardId) => {
    try {
      const cardNames = {
        1: "Career_Stats",
        2: "Spotify_Wrapped",
        3: "Netflix_Style",
        4: "GitHub_Style",
        5: "Spotify_Desktop",
        6: "Instagram_Story",
        7: "Duolingo_Style",
        8: "Spotify_Wrapped_Summary",
        9: "Apple_Music_Desktop",
        10: "Discord_Desktop",
        11: "GitHub_Profile",
        12: "Spotify_Podcasts",
        13: "IMDb_Professional",
        14: "YouTube_Studio",
      };

      // Download single card
      const cardName = cardNames[cardId] || `Card${cardId}`;
      await downloadCardAsImage(fullSizeCardRef, cardName);
      message.success("Card downloaded successfully!");
    } catch (error) {
      message.destroy();
      logger.error("Failed to download card", {
        extras: {
          error: error.message || error,
          cardNumber: cardId,
          context: "card_download_error",
        },
      });
      message.error("Failed to download card. Please try again.");
    }
  };

  const handlePhotoUploaded = (photoUrl) => {
    setProfilePhotoUrl(photoUrl);
    const successMessage = photoUrl
      ? "Profile photo updated successfully! Your resume preview has been updated."
      : "Profile photo removed successfully!";
    message.success(successMessage);

    // Refresh resume data to update photo URL
    fetchResumeData();
  };

  if (loading) {
    return (
      <AppLayout>
        <Layout>
          <Content style={{ padding: "24px" }}>
            <Title level={2}>Resume Preview</Title>
            <Card>
              <Skeleton active paragraph={{ rows: 10 }} />
            </Card>
          </Content>
        </Layout>
      </AppLayout>
    );
  }

  if (error || !resumeData) {
    return (
      <AppLayout>
        <Layout>
          <Content style={{ padding: "24px" }}>
            <Title level={2}>Resume Preview</Title>
            <Card>
              <Empty
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                description={
                  <div>
                    <Title level={4}>Resume not found</Title>
                    <Text>The resume could not be loaded. Please try uploading a new one.</Text>
                  </div>
                }
              />
            </Card>
          </Content>
        </Layout>
      </AppLayout>
    );
  }

  // Show thank you message if showThankYou is true
  if (false) {
    // Disabled - now using dedicated thank you page
    return (
      <Layout>
        <Content style={{ padding: "24px", minHeight: "100vh" }}>
          <div
            style={{
              maxWidth: "600px",
              margin: "0 auto",
              paddingTop: "60px",
            }}
          >
            <Result
              icon={<CheckCircleOutlined style={{ color: "#52c41a", fontSize: "72px" }} />}
              title={
                <Title level={2} style={{ color: "#52c41a", marginBottom: "16px" }}>
                  Thank You for Using Our AI Resume Builder!
                </Title>
              }
              subTitle={
                <Space
                  direction="vertical"
                  size="large"
                  style={{ width: "100%", maxWidth: "500px", margin: "0 auto" }}
                >
                  <Typography.Paragraph
                    style={{
                      fontSize: "16px",
                      color: "#595959",
                      textAlign: "center",
                      marginBottom: "24px",
                    }}
                  >
                    Your professional resume has been successfully created and downloaded to your device.
                  </Typography.Paragraph>

                  <Card
                    size="small"
                    style={{
                      textAlign: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <Space direction="vertical" size="middle">
                      <Typography.Title level={4} style={{ color: "#1890ff", margin: 0 }}>
                        Your Resume is Ready!
                      </Typography.Title>
                      <Typography.Text style={{ color: "#8c8c8c" }}>
                        The PDF has been optimized for ATS systems and is ready for job applications.
                      </Typography.Text>
                    </Space>
                  </Card>

                  <Card
                    size="small"
                    style={{
                      textAlign: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      border: "1px solid #f0f0f0",
                      marginTop: "16px",
                    }}
                  >
                    <Space direction="vertical" size="middle">
                      <Typography.Title level={4} style={{ color: "#0077b5", margin: 0 }}>
                        <ShareAltOutlined style={{ marginRight: "8px" }} />
                        Share Your Success
                      </Typography.Title>
                      <Typography.Text style={{ color: "#8c8c8c", marginBottom: "12px" }}>
                        Let your network know about your professional achievement!
                      </Typography.Text>
                      <Button
                        type="primary"
                        icon={<LinkedinOutlined />}
                        onClick={handleLinkedInShare}
                        style={{
                          backgroundColor: "#0077b5",
                          borderColor: "#0077b5",
                          borderRadius: "6px",
                        }}
                        size="large"
                      >
                        Share on LinkedIn
                      </Button>
                    </Space>
                  </Card>
                </Space>
              }
              extra={
                <Space size="middle">
                  <Button type="default" icon={<HomeOutlined />} onClick={handleGoHome} size="large">
                    Dashboard
                  </Button>
                  <Button type="primary" icon={<EditOutlined />} onClick={handleCreateAnother} size="large">
                    Create New Resume
                  </Button>
                </Space>
              }
            />

            <div style={{ textAlign: "center", marginTop: "32px", paddingBottom: "40px" }}>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                Best of luck with your job applications!
              </Typography.Text>
            </div>
          </div>
        </Content>
      </Layout>
    );
  }

  const handleBack = () => {
    navigate(`/app/resume/${resumeId}`);
  };

  return (
    <AppLayout>
      <style>{`
        /* Simple CSS Reset to prevent page stretching */
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          overflow-x: hidden !important;
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
        }

        *, *::before, *::after {
          box-sizing: border-box !important;
        }
      `}</style>
      <Layout
        style={{
          height: "100vh",
          overflow: "hidden",
        }}
      >
        <Content
          style={{
            padding: "40px 0 24px 0",
            height: "100vh",
            overflowY: "auto",
            position: "relative",
          }}
        >
          {/* Show template selection or preview based on selectedTemplate */}
          {!selectedTemplate ? (
            <>
              {/* Header with back button */}
              <div
                style={{
                  position: "absolute",
                  top: "50px",
                  left: "80px",
                  zIndex: 10,
                }}
              >
                <Button
                  type="default"
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBack}
                  className="back-to-options-btn"
                  style={{
                    fontSize: "14px",
                    borderColor: "#1890ff",
                    color: "#1890ff",
                    backgroundColor: "transparent",
                    height: "36px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    borderRadius: "6px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "all 0.3s ease",
                  }}
                >
                  Back to Editor
                </Button>
              </div>

              <Space direction="vertical" size="large" style={{ width: "100%", marginTop: "32px" }}>
                {/* Template Selection */}
                <Card>
                  <Space direction="vertical" size="large" style={{ width: "100%" }}>
                    <div style={{ textAlign: "center" }}>
                      <Title level={3}>
                        Choose Your {viewMode === "resume" ? "Resume" : "Cover Letter"} Template
                      </Title>
                      <Text type="secondary">
                        {viewMode === "resume"
                          ? "Select a template that best represents your professional style"
                          : "Select a template for your cover letter"}
                      </Text>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                        gap: "20px",
                      }}
                    >
                      {templates.map((template) => (
                        <TemplateCard
                          key={template.id}
                          template={template}
                          onPreview={handleTemplateSelect}
                          resumeData={resumeData}
                          isLoading={templatesLoading[template.id]}
                        />
                      ))}

                      {/* Preload all templates in the background */}
                      <TemplatePreloader
                        resumeData={resumeData}
                        onTemplateReady={handleTemplateReady}
                        templatesLoading={templatesLoading}
                      />
                    </div>
                  </Space>
                </Card>
              </Space>
            </>
          ) : (
            <>
              {/* Template Preview */}
              {/* Back to Templates button in top left - only show when not in share mode */}
              {!showShareRequired && (
                <div
                  style={{
                    position: "absolute",
                    top: "20px",
                    left: "20px",
                    zIndex: 10,
                  }}
                >
                  <Button
                    type="default"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBackToTemplates}
                    style={{
                      fontSize: "14px",
                      borderColor: "#1890ff",
                      color: "#1890ff",
                      backgroundColor: "transparent",
                      height: "36px",
                      paddingLeft: "16px",
                      paddingRight: "16px",
                      borderRadius: "6px",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Back to Templates
                  </Button>
                </div>
              )}

              {/* Download Resume button in top right */}
              {/* Hide top-right download button when sharing is required */}
              {!showShareRequired && (
                <div
                  style={{
                    position: "absolute",
                    top: "20px",
                    right: "20px",
                    zIndex: 10,
                    display: "flex",
                    gap: "12px",
                  }}
                >
                  {/* Add/Edit Photo button - shows when using photo, modern_slate, photo_classic or executive_sidebar template */}
                  {(selectedTemplate === "photo" || selectedTemplate === "modern_slate" || selectedTemplate === "photo_classic" || selectedTemplate === "executive_sidebar") && (
                    <ProfilePhotoUpload
                      resumeId={resumeId}
                      currentPhotoUrl={profilePhotoUrl}
                      onPhotoUploaded={handlePhotoUploaded}
                      trigger={true}
                      mode={profilePhotoUrl ? "edit" : "upload"}
                    />
                  )}
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadClick}
                    style={{
                      backgroundColor: "#1890ff",
                      borderColor: "#1890ff",
                      color: "white",
                      height: "36px",
                      fontSize: "14px",
                      paddingLeft: "16px",
                      paddingRight: "16px",
                      borderRadius: "6px",
                      fontWeight: "500",
                    }}
                  >
                    Download Resume
                  </Button>
                </div>
              )}

              {/* Show Template Preview or Share Content based on showShareRequired */}
              {!showShareRequired ? (
                <div style={{ paddingTop: "30px" }}>
                  {resumeData && viewMode === "resume" && (
                    <CustomizableResumePreview
                      ref={customizableResumeRef}
                      resumeData={resumeData}
                      selectedTemplate={templates.find((t) => t.id === selectedTemplate)}
                      resumeId={resumeId}
                      onClose={handleBackToTemplates}
                      onPrint={handlePrintFromPreview}
                      preloadedUrl={preloadedTemplates[selectedTemplate]}
                      onCustomizationChange={setCurrentCustomizations}
                      onSectionOrderChange={setCurrentSectionOrder}
                      onPhotoUploaded={handlePhotoUploaded}
                    />
                  )}
                  {resumeData && viewMode === "coverLetter" && resumeData?.coverLetter?.generatedContent && (
                    <div
                      style={{
                        maxWidth: "800px",
                        margin: "0 auto",
                        padding: "40px",
                        backgroundColor: "#ffffff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        borderRadius: "8px",
                      }}
                    >
                      <div
                        style={{
                          marginBottom: "24px",
                          paddingBottom: "16px",
                          borderBottom: "2px solid #e8e8e8",
                        }}
                      >
                        <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
                          Cover Letter Preview
                        </Title>
                        <Text type="secondary">
                          {resumeData.coverLetter.targetRole} at {resumeData.coverLetter.companyName}
                        </Text>
                      </div>
                      <div
                        style={{
                          whiteSpace: "pre-wrap",
                          lineHeight: "1.8",
                          fontSize: "14px",
                          color: "#333",
                        }}
                      >
                        {resumeData.coverLetter.generatedContent}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Share Content - shown directly on page */
                <div
                  style={{
                    paddingTop: "0px",
                    width: "100%",
                    margin: "0 auto",
                    minHeight: "100vh",
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: "0px",
                      padding: "12px 20px 12px 20px",
                      boxShadow: "none",
                      marginTop: "0px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Share Section - Absolute Top with Back Button */}
                    <div style={{ marginBottom: "8px", paddingTop: "4px", flexShrink: 0 }}>
                      {/* Back to Preview button and Title in same row */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                          gap: "12px",
                        }}
                      >
                        <Button
                          icon={<ArrowLeftOutlined />}
                          onClick={handleCloseShareModal}
                          style={{
                            borderColor: "#1890ff",
                            color: "#1890ff",
                            backgroundColor: "transparent",
                            height: "32px",
                            fontSize: "13px",
                            paddingLeft: "12px",
                            paddingRight: "12px",
                            borderRadius: "6px",
                            fontWeight: "500",
                            flexShrink: 0,
                          }}
                        >
                          Back to Preview
                        </Button>

                        <Typography.Title
                          level={4}
                          style={{
                            color: "#1e293b",
                            margin: "0px",
                            flex: 1,
                            textAlign: "center",
                            fontSize: "18px",
                          }}
                        >
                          Share a Card on LinkedIn to Unlock Download
                        </Typography.Title>

                        {/* Invisible placeholder to balance the layout */}
                        <div style={{ width: "120px", flexShrink: 0 }}></div>
                      </div>

                      {/* Download Button - Show after any card URL is submitted or if already shared */}
                      {hasShared && (
                        <div style={{ marginTop: "16px", textAlign: "center" }}>
                          {resumeData && (
                            <Button
                              type="primary"
                              icon={<DownloadOutlined />}
                              loading={downloadLoading}
                              onClick={handleDownloadBoth}
                              size="large"
                              style={{
                                backgroundColor: "#1890ff",
                                borderColor: "#1890ff",
                                color: "white",
                                height: "48px",
                                fontSize: "16px",
                                paddingLeft: "24px",
                                paddingRight: "24px",
                                borderRadius: "8px",
                                fontWeight: "600",
                                minWidth: "200px",
                              }}
                            >
                              {downloadLoading
                                ? "Generating PDFs..."
                                : resumeData?.coverLetter?.generatedContent
                                ? "Download Resume & Cover Letter"
                                : "Download Resume"}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Shareable Cards Section */}
                    <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                      {/* Card Grid - Actual Previews */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: "16px",
                          width: "100%",
                          marginTop: "15px",
                          maxWidth: "1200px",
                          margin: "0 auto",
                          overflow: "hidden",
                        }}
                      >
                        {[
                          {
                            id: 1,
                            title: "Career Stats Avatar",
                            subtitle: "LinkedIn-style career avatar highlights",
                          },
                          {
                            id: 5,
                            title: "Spotify Desktop Avatar",
                            subtitle: "Spotify desktop-style avatar profile",
                          },
                          {
                            id: 3,
                            title: "Netflix Style Avatar",
                            subtitle: "Netflix-style professional avatar showcase",
                          },
                          {
                            id: 9,
                            title: "Apple Music Avatar",
                            subtitle: "Apple Music-style avatar now playing card",
                          },
                          {
                            id: 10,
                            title: "Discord Avatar",
                            subtitle: "Discord-style server avatar resume showcase",
                          },
                          {
                            id: 11,
                            title: "GitHub Profile Avatar",
                            subtitle: "GitHub profile avatar with contribution graph",
                          },
                          {
                            id: 12,
                            title: "Spotify Podcasts Avatar",
                            subtitle: "Spotify Podcasts-style avatar episode showcase",
                          },
                          {
                            id: 13,
                            title: "IMDb Professional Avatar",
                            subtitle: "IMDb-style professional avatar profile showcase",
                          },
                          {
                            id: 14,
                            title: "YouTube Studio Avatar",
                            subtitle: "YouTube Studio avatar analytics dashboard",
                          },
                          {
                            id: 15,
                            title: "Twitter/X Avatar",
                            subtitle: "Twitter desktop-style viral avatar tweet showcase",
                          },
                          {
                            id: 6,
                            title: "Instagram Story Avatar",
                            subtitle: "Instagram story-style professional avatar card",
                          },
                          {
                            id: 7,
                            title: "Duolingo Avatar",
                            subtitle: "Duolingo-style skill mastery avatar showcase",
                          },
                          {
                            id: 8,
                            title: "Spotify Wrapped Avatar",
                            subtitle: "All-in-one Spotify Wrapped avatar card",
                          },
                        ].map((cardInfo) => (
                          <div
                            key={cardInfo.id}
                            style={{
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                              padding: "16px",
                              backgroundColor: "white",
                              transition: "all 0.3s ease",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                              position: "relative",
                              display: "flex",
                              flexDirection: "column",
                              height: "fit-content",
                              minWidth: 0,
                              overflow: "hidden",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                              e.currentTarget.style.transform = "translateY(-2px)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.12)";
                              e.currentTarget.style.transform = "translateY(0)";
                            }}
                          >
                            {/* Card Title with Action Buttons */}
                            <div
                              style={{
                                marginBottom: "12px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                paddingBottom: "12px",
                                borderBottom: "1px solid #e5e7eb",
                              }}
                            >
                              <Typography.Title
                                level={5}
                                style={{ margin: "0", color: "#1e293b", fontSize: "16px", fontWeight: "600" }}
                              >
                                {cardInfo.title}
                              </Typography.Title>
                              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                {/* Add/Edit Photo Button */}
                                <ProfilePhotoUpload
                                  resumeId={resumeId}
                                  currentPhotoUrl={profilePhotoUrl}
                                  onPhotoUploaded={handlePhotoUploaded}
                                  trigger={
                                    <Button
                                      type="default"
                                      icon={<UserOutlined />}
                                      size="small"
                                      style={{
                                        borderColor: "#1890ff",
                                        color: "#1890ff",
                                        fontSize: "12px",
                                        padding: "4px 12px",
                                        height: "32px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        fontWeight: "600",
                                      }}
                                      title={
                                        profilePhotoUrl ? "Edit your profile photo" : "Add your profile photo"
                                      }
                                    >
                                      {profilePhotoUrl ? "Edit Photo" : "Add Photo"}
                                    </Button>
                                  }
                                  mode={profilePhotoUrl ? "edit" : "upload"}
                                />
                                <Button
                                  onClick={() => handleCardLinkedInShare(cardInfo.id)}
                                  style={{
                                    backgroundColor: "#0077b5",
                                    borderColor: "#0077b5",
                                    borderRadius: "50%",
                                    width: "36px",
                                    height: "36px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "0",
                                    border: "none",
                                    flexShrink: 0,
                                  }}
                                  title="Share on LinkedIn"
                                >
                                  <LinkedinOutlined style={{ fontSize: "18px", color: "white" }} />
                                </Button>
                                <Button
                                  type="default"
                                  icon={<ExportOutlined />}
                                  size="small"
                                  onClick={() => handleCopyDescription(cardInfo.id)}
                                  style={{
                                    borderColor: "#d9d9d9",
                                    color: "#1890ff",
                                    fontSize: "12px",
                                    padding: "4px 12px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  Copy
                                </Button>
                                <Button
                                  type="primary"
                                  icon={<DownloadOutlined />}
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCard(cardInfo.id);
                                    setTimeout(() => handleDownloadCard(cardInfo.id), 100);
                                  }}
                                  style={{
                                    backgroundColor: "#1890ff",
                                    borderColor: "#1890ff",
                                    fontSize: "12px",
                                    padding: "4px 12px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  Download
                                </Button>
                              </div>
                            </div>

                            {/* LinkedIn URL Input Section - Show for specific card */}
                            {cardLinkedInClicked[cardInfo.id] && !hasShared && (
                              <div
                                style={{
                                  marginBottom: "12px",
                                  padding: "12px",
                                  backgroundColor: "#f0f5ff",
                                  borderRadius: "6px",
                                  border: "1px solid #d6e4ff",
                                }}
                              >
                                <Typography.Text
                                  style={{
                                    fontSize: "13px",
                                    color: "#1890ff",
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: "500",
                                  }}
                                >
                                  Paste your LinkedIn post URL below:
                                </Typography.Text>
                                <div style={{ display: "flex", gap: "8px" }}>
                                  <Input
                                    placeholder="Paste the LinkedIn URL where you shared..."
                                    value={cardSharedUrl[cardInfo.id] || ""}
                                    onChange={(e) =>
                                      setCardSharedUrl((prev) => ({
                                        ...prev,
                                        [cardInfo.id]: e.target.value,
                                      }))
                                    }
                                    size="middle"
                                    style={{
                                      borderRadius: "6px",
                                      flex: 1,
                                    }}
                                    onPressEnter={() => handleCardUrlSubmit(cardInfo.id)}
                                  />
                                  <Button
                                    type="primary"
                                    size="middle"
                                    onClick={() => handleCardUrlSubmit(cardInfo.id)}
                                    disabled={!cardSharedUrl[cardInfo.id] || !cardSharedUrl[cardInfo.id].trim()}
                                    style={{
                                      backgroundColor: "#1890ff",
                                      borderColor: "#1890ff",
                                      borderRadius: "6px",
                                      fontWeight: "600",
                                      minWidth: "80px",
                                    }}
                                  >
                                    Submit
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Description Box */}
                            <div
                              style={{
                                backgroundColor: "#f9fafb",
                                borderRadius: "6px",
                                padding: "12px",
                                marginBottom: "12px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#374151",
                                  lineHeight: "1.6",
                                  whiteSpace: "pre-wrap",
                                  userSelect: "text",
                                  WebkitUserSelect: "text",
                                  MozUserSelect: "text",
                                  msUserSelect: "text",
                                }}
                              >
                                {getCardDescription(cardInfo.id)}
                              </div>
                            </div>

                            {/* Card Preview - Fully Fitted */}
                            <div
                              style={{
                                backgroundColor: "#f8fafc",
                                borderRadius: "6px",
                                overflow: "hidden",
                                marginBottom: "12px",
                                height:
                                  cardInfo.id === 1
                                    ? "220px"
                                    : cardInfo.id === 3
                                    ? "184px"
                                    : cardInfo.id === 5
                                    ? "184px"
                                    : cardInfo.id === 6
                                    ? "640px"
                                    : cardInfo.id === 7
                                    ? "640px"
                                    : cardInfo.id === 8
                                    ? "640px"
                                    : cardInfo.id === 9
                                    ? "220px"
                                    : cardInfo.id === 10
                                    ? "220px"
                                    : cardInfo.id === 11
                                    ? "220px"
                                    : cardInfo.id === 12
                                    ? "220px"
                                    : cardInfo.id === 13
                                    ? "220px"
                                    : cardInfo.id === 14
                                    ? "220px"
                                    : cardInfo.id === 15
                                    ? "220px"
                                    : "380px",
                                position: "relative",
                                width: "100%",
                              }}
                            >
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    transform: `scale(${
                                      cardInfo.id === 1
                                        ? 0.2
                                        : cardInfo.id === 3
                                        ? 0.3
                                        : cardInfo.id === 5
                                        ? 0.3
                                        : cardInfo.id === 6
                                        ? 0.33
                                        : cardInfo.id === 7
                                        ? 0.33
                                        : cardInfo.id === 8
                                        ? 0.33
                                        : cardInfo.id === 9
                                        ? 0.2
                                        : cardInfo.id === 10
                                        ? 0.2
                                        : cardInfo.id === 11
                                        ? 0.2
                                        : cardInfo.id === 12
                                        ? 0.2
                                        : cardInfo.id === 13
                                        ? 0.2
                                        : cardInfo.id === 14
                                        ? 0.2
                                        : cardInfo.id === 15
                                        ? 0.2
                                        : 0.35
                                    })`,
                                    transformOrigin: "center center",
                                    width:
                                      cardInfo.id === 1
                                        ? "1920px"
                                        : cardInfo.id === 3
                                        ? "1275px"
                                        : cardInfo.id === 5
                                        ? "1275px"
                                        : cardInfo.id === 6
                                        ? "1080px"
                                        : cardInfo.id === 7
                                        ? "1080px"
                                        : cardInfo.id === 8
                                        ? "1080px"
                                        : cardInfo.id === 9
                                        ? "1920px"
                                        : cardInfo.id === 10
                                        ? "1920px"
                                        : cardInfo.id === 11
                                        ? "1920px"
                                        : cardInfo.id === 12
                                        ? "1920px"
                                        : cardInfo.id === 13
                                        ? "1920px"
                                        : cardInfo.id === 14
                                        ? "1920px"
                                        : cardInfo.id === 15
                                        ? "1920px"
                                        : "1080px",
                                    height:
                                      cardInfo.id === 1
                                        ? "1080px"
                                        : cardInfo.id === 3
                                        ? "614px"
                                        : cardInfo.id === 5
                                        ? "614px"
                                        : cardInfo.id === 6
                                        ? "1920px"
                                        : cardInfo.id === 7
                                        ? "1920px"
                                        : cardInfo.id === 8
                                        ? "1920px"
                                        : cardInfo.id === 9
                                        ? "1080px"
                                        : cardInfo.id === 10
                                        ? "1080px"
                                        : cardInfo.id === 11
                                        ? "1080px"
                                        : cardInfo.id === 12
                                        ? "1080px"
                                        : cardInfo.id === 13
                                        ? "1080px"
                                        : cardInfo.id === 14
                                        ? "1080px"
                                        : cardInfo.id === 15
                                        ? "1080px"
                                        : "1080px",
                                    pointerEvents: "none",
                                  }}
                                >
                                  {cardInfo.id === 1 && (
                                    <CareerStatsCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                                  )}
                                  {cardInfo.id === 5 && (
                                    <SpotifyDesktopCard
                                      resumeData={resumeData}
                                      profilePhotoUrl={profilePhotoUrl}
                                    />
                                  )}
                                  {cardInfo.id === 3 && (
                                    <NetflixStyleCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                                  )}
                                  {cardInfo.id === 4 && (
                                    <GitHubStyleCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                                  )}
                                  {cardInfo.id === 2 && (
                                    <SpotifyWrappedCard
                                      resumeData={resumeData}
                                      profilePhotoUrl={profilePhotoUrl}
                                    />
                                  )}
                                  {cardInfo.id === 6 && (
                                    <InstagramStoryCard
                                      resumeData={resumeData}
                                      profilePhotoUrl={profilePhotoUrl}
                                    />
                                  )}
                                  {cardInfo.id === 7 && (
                                    <DuolingoCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                                  )}
                                  {cardInfo.id === 8 && (
                                    <SpotifyWrappedMobileSummaryCard
                                      resumeData={resumeData}
                                      profilePhotoUrl={profilePhotoUrl}
                                    />
                                  )}
                                  {cardInfo.id === 9 && (
                                    <AppleMusicDesktopCard
                                      resumeData={resumeData}
                                      profilePhotoUrl={profilePhotoUrl}
                                    />
                                  )}
                                  {cardInfo.id === 10 && (
                                    <DiscordDesktopCard
                                      resumeData={resumeData}
                                      profilePhotoUrl={profilePhotoUrl}
                                    />
                                  )}
                                  {cardInfo.id === 11 && (
                                    <GitHubProfileCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                                  )}
                                  {cardInfo.id === 12 && (
                                    <SpotifyPodcastsDesktopCard
                                      resumeData={resumeData}
                                      profilePhotoUrl={profilePhotoUrl}
                                    />
                                  )}
                                  {cardInfo.id === 13 && (
                                    <IMDBDesktopCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                                  )}
                                  {cardInfo.id === 14 && (
                                    <YouTubeStudioCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                                  )}
                                  {cardInfo.id === 15 && (
                                    <TwitterMobileCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Hidden full-size cards for download */}
                      <div
                        style={{
                          position: "absolute",
                          left: "-9999px",
                          top: "0",
                        }}
                      >
                        <div ref={fullSizeCardRef}>
                          {selectedCard === 1 && (
                            <CareerStatsCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                          {selectedCard === 5 && (
                            <SpotifyDesktopCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                          {selectedCard === 3 && (
                            <NetflixStyleCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                          {selectedCard === 4 && (
                            <GitHubStyleCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                          {selectedCard === 2 && (
                            <SpotifyWrappedCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                          {selectedCard === 6 && (
                            <InstagramStoryCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                          {selectedCard === 7 && (
                            <DuolingoCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                          {selectedCard === 8 && (
                            <SpotifyWrappedMobileSummaryCard
                              resumeData={resumeData}
                              profilePhotoUrl={profilePhotoUrl}
                            />
                          )}
                          {selectedCard === 9 && (
                            <AppleMusicDesktopCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                          {selectedCard === 10 && (
                            <DiscordDesktopCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                          {selectedCard === 11 && (
                            <GitHubProfileCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                          {selectedCard === 12 && (
                            <SpotifyPodcastsDesktopCard
                              resumeData={resumeData}
                              profilePhotoUrl={profilePhotoUrl}
                            />
                          )}
                          {selectedCard === 13 && (
                            <IMDBDesktopCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                          {selectedCard === 14 && (
                            <YouTubeStudioCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                          {selectedCard === 15 && (
                            <TwitterMobileCard resumeData={resumeData} profilePhotoUrl={profilePhotoUrl} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Content>
      </Layout>
    </AppLayout>
  );
};

export default ResumePreview;
